import { toast } from '@systemeio/ui'
import { BadRequest, InternalError } from '@systemeio/util'
import { PRODUCTS_API, updateProduct } from 'modules/products/api/product-api'
import ProductForm from 'modules/products/components/product-form'
import { emptyProductErrorsState } from 'modules/products/constants/product-errors'
import { ProductsLoco } from 'modules/products/constants/products-loco'
import { useMaxVariants } from 'modules/products/hooks/use-max-variants'
import { useProduct } from 'modules/products/hooks/use-products'
import { ProductErrorResponseContentInterface } from 'modules/products/types/error-response-content-interface'
import { ProductApiInterface, ProductSaveInterface } from 'modules/products/types/product-interface'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useMutationUpdate } from 'shared/hooks/use-mutation-update'
import { useUpdateData } from 'shared/hooks/use-update-data'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

function ProductPage() {
  const { t } = useLocoTranslation()

  const [isFetching, setIsFetching] = useState(false)
  const [validationErrorsNotification, setValidationErrorsNotification] = useState(false)

  const router = useRouter()
  const { productId } = router.query
  const { data: product, isValidating, mutate } = useProduct(Number(productId))
  const { maxVariants } = useMaxVariants()

  const updateProductListData = useUpdateData({
    api: PRODUCTS_API,
  })
  const mutationUpdate = useMutationUpdate()

  const handleUpdateProduct = async (productSaveData: ProductSaveInterface) => {
    if (!product) return

    setIsFetching(true)

    const { productData, setErrors, setVariantErrors } = productSaveData

    try {
      setErrors(emptyProductErrorsState)
      setValidationErrorsNotification(false)
      await updateProduct(Number(product.id), productData)
      setIsFetching(false)
      await updateProductListData(
        false,
        undefined,
        {
          mutationData: product,
          onMutate: async () => {
            // @ts-expect-error
            await mutate(data => {
              if (data) {
                return {
                  ...data,
                  ...productData,
                }
              }
            }, false)
          },
        },
        {
          id: product.id,
          onMutate: async apiKey => {
            await mutationUpdate<ProductApiInterface>(
              apiKey,
              data =>
                data && {
                  total: data.total,
                  hasMore: data.hasMore,
                  items: data.items.map(item => {
                    if (item.id === product.id)
                      item = {
                        ...item,
                        ...productData,
                      }
                    return item
                  }),
                },
            )
          },
        },
      )
      toast.success(t('global.changes_saved'))
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const productErrors = e.errors as unknown as ProductErrorResponseContentInterface
        setErrors({
          name: productErrors.fields.name?.join(''),
          currency: productErrors.fields.currency?.join(''),
          price: productErrors.fields.price?.join(''),
          sku: productErrors.fields.sku?.join(''),
          tax: productErrors.fields.tax?.join(''),
          weight: productErrors.fields.weight?.join(''),
          common: productErrors.common,
          options: (productErrors.fields.options as Record<string, Record<string, string[]>>) || {},
          stock: productErrors.fields.stock?.join(''),
          shippingFeesDisabled: productErrors.fields.shippingFeesDisabled?.join(''),
          taxBehavior: productErrors.fields.taxBehavior?.join(''),
        })

        if (!!productErrors.fields?.productVariants) {
          setVariantErrors(productErrors.fields.productVariants)
        }

        if (!!productErrors.fields?.productVariantsLimit) {
          toast.error(productErrors.fields.productVariantsLimit?.join(''))
        }

        if (!!Object.values(productErrors.fields).length) setValidationErrorsNotification(true)
      } else if (e instanceof InternalError) {
        setErrors(prev => {
          return {
            ...prev,
            common: [t('core.error.internal_error_message')],
          }
        })
      }
    }
  }

  if (!product || isValidating || !maxVariants) {
    return (
      <div className="m-auto table">
        <Loader type="dark" />
      </div>
    )
  }

  return (
    <ProductForm
      // @ts-expect-error
      product={product}
      validationErrorsNotification={validationErrorsNotification}
      isFetching={isFetching}
      onSave={handleUpdateProduct}
      maxProductVariants={maxVariants.maxProductVariants}
      setValidationErrorsNotification={setValidationErrorsNotification}
    />
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ProductsLoco,
  'header.top_menu.physical_product',
)

export default ProductPage
